import { memo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "components/DialogV2";
import DialogHeader from "components/DialogV2/DialogHeader";
import DialogTitle from "components/DialogV2/DialogTitle";
import DialogClose from "components/DialogV2/DialogClose";
import { CarouselSlides } from "components/Carousel/components";
import Typography from "components/Typography";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import { athlete1RMV2Enabled, athleteProfilingV2Enabled } from "config/features";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import useVelocityOnBoarding from "hooks/useVelocityOnBoarding";
import logEvent from "utils/logEvent";
import profilingVideo from "assets/videos/athlete-velocity-announcement-profiling-720.mp4";
import oneRepMaxVideo from "assets/videos/athlete-velocity-announcement-1rm-720.mp4";
import {
  AthleteVelocityAnnouncementCarousel,
  AthleteVelocityAnnouncementContent,
  AthleteVelocityAnnouncementFooter,
  AthleteVelocityAnnouncementNextButton,
  AthleteVelocityAnnouncementSlide,
} from "./components";

/** Walks the user through the velocity-based charting updates on the athlete profile, once. */
function AthleteVelocityAnnouncementDialog() {
  const { t } = useTranslation();
  const { open, dismiss } = useVelocityOnBoarding(LocalStorageHelper.keys.showAthleteVelocityModal);

  useEffect(() => {
    if (!open) return;

    logEvent("ATHLETE_VELOCITY_ANNOUNCEMENT_VIEW");
  }, [open]);

  const dismissFrom = useCallback(
    (origin: "closeButton" | "escapeKey" | "gotItButton") => {
      logEvent("ATHLETE_VELOCITY_ANNOUNCEMENT_DISMISS", { origin });
      dismiss();
    },
    [dismiss]
  );

  const handleClose = useCallback(
    (_event: React.MouseEvent, reason?: "backdropClick" | "escapeKeyDown") =>
      dismissFrom(reason === "escapeKeyDown" ? "escapeKey" : "closeButton"),
    [dismissFrom]
  );

  const handleFinish = useCallback(() => dismissFrom("gotItButton"), [dismissFrom]);

  return (
    <ThemeProvider2024>
      <Dialog open={open} onClose={handleClose} size="md" width={630} disableBackdropClose>
        <DialogHeader>
          <DialogTitle>{t("athlete.velocity_announcement_title")}</DialogTitle>
        </DialogHeader>
        <DialogClose />
        <AthleteVelocityAnnouncementContent>
          <AthleteVelocityAnnouncementCarousel aria-label={t("athlete.velocity_announcement_title")}>
            <CarouselSlides>
              {athleteProfilingV2Enabled ? (
                <AthleteVelocityAnnouncementSlide
                  key="profiling"
                  video={profilingVideo}
                  videoAlt={t("athlete.velocity_announcement_profiling_image_alt")}
                  heading={t("athlete_profile.profiling_velocity_onboarding_title")}
                >
                  <Typography>{t("athlete.velocity_announcement_profiling_description")}</Typography>
                  <Typography>{t("athlete_profile.profiling_velocity_onboarding_comparison")}</Typography>
                </AthleteVelocityAnnouncementSlide>
              ) : null}
              {athlete1RMV2Enabled ? (
                <AthleteVelocityAnnouncementSlide
                  key="one-rep-max"
                  video={oneRepMaxVideo}
                  videoAlt={t("athlete.velocity_announcement_one_rep_max_image_alt")}
                  heading={t("athlete.velocity_announcement_one_rep_max_heading")}
                >
                  <Typography>{t("athlete.velocity_announcement_one_rep_max_description")}</Typography>
                  <Typography>{t("athlete.velocity_announcement_one_rep_max_manual")}</Typography>
                </AthleteVelocityAnnouncementSlide>
              ) : null}
            </CarouselSlides>
            <AthleteVelocityAnnouncementFooter>
              <AthleteVelocityAnnouncementNextButton onFinish={handleFinish} />
            </AthleteVelocityAnnouncementFooter>
          </AthleteVelocityAnnouncementCarousel>
        </AthleteVelocityAnnouncementContent>
      </Dialog>
    </ThemeProvider2024>
  );
}

export default memo(AthleteVelocityAnnouncementDialog);
